import type { VideoModelId, VideoTaskStatus } from "@bianhua/protocol";

const VIDEO_MODEL_IDS = [
  "grok-imagine-video",
  "doubao-seedance-2-0-260128",
  "doubao-seedance-2-0-fast-260128",
  "doubao-seedance-2-5-260628",
] as const satisfies readonly VideoModelId[];

const VIDEO_TASK_STATUSES = [
  "queued",
  "running",
  "cancelled",
  "succeeded",
  "failed",
  "expired",
] as const satisfies readonly VideoTaskStatus[];

export const VIDEO_RATIOS = [
  "adaptive",
  "16:9",
  "4:3",
  "1:1",
  "3:4",
  "9:16",
  "3:2",
  "2:3",
] as const;

export type VideoRatio = (typeof VIDEO_RATIOS)[number];
export type VideoMethod = "text" | "image" | "reference";
export type VideoResolution = "480p" | "720p" | "1080p" | "4k";

export type VideoDraft = {
  prompt: string;
  modelId: VideoModelId;
  method: VideoMethod;
  ratio: VideoRatio;
  durationSeconds: -1 | number;
  generateAudio: boolean;
  resolution: VideoResolution;
  assetIds: string[];
};

export type VideoCreateRequest = VideoDraft & {
  requestId: string;
};

export type VideoControlState =
  | "unconfigured"
  | "ready"
  | "submitting"
  | "recovering"
  | "cancelling"
  | "archiving"
  | VideoTaskStatus;

export type VideoControlSnapshot = {
  state: VideoControlState;
  taskId: string | null;
  requestId: string | null;
  modelId: VideoModelId | null;
  reason: string;
  canCreate: boolean;
  canCancelQueued: boolean;
  canStopRunning: false;
};

export type RestoredVideoTask = {
  taskId: string;
  state: "queued" | "running" | "recovering" | "archiving" | "cancelled" | "succeeded" | "failed" | "expired";
  requestId?: string | null;
  modelId?: VideoModelId | null;
  reason?: string;
};

function oneOf<const T extends readonly string[]>(value: unknown, values: T): value is T[number] {
  return typeof value === "string" && values.includes(value as T[number]);
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeAudio(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  return !["none", "off", "false", "无音频", "关闭"].includes(stringValue(value).toLowerCase());
}

export function normalizeVideoDraft(value: unknown): VideoDraft {
  if (!value || typeof value !== "object") throw new Error("视频生成参数无效");
  const draft = value as Record<string, unknown>;
  const prompt = stringValue(draft.prompt);
  if (!prompt) throw new Error("请先输入视频创作要求");
  if (prompt.length > 20_000) throw new Error("视频创作要求超过 20000 个字符");

  if (!oneOf(draft.modelId, VIDEO_MODEL_IDS)) throw new Error("不支持该视频模型");
  const modelId = draft.modelId;
  if (!oneOf(draft.method, ["text", "image", "reference"] as const)) throw new Error("不支持该视频创作方式");
  if (!oneOf(draft.ratio, VIDEO_RATIOS)) throw new Error("不支持该视频比例");
  const resolutions = modelId === "doubao-seedance-2-0-260128"
    ? (["480p", "720p", "1080p", "4k"] as const)
    : (["480p", "720p"] as const);
  if (!oneOf(draft.resolution, resolutions)) throw new Error("该视频模型不支持所选视频分辨率");

  const duration = Number(draft.durationSeconds);
  const minDuration = modelId === "grok-imagine-video" ? 1 : 4;
  const maxDuration = modelId === "doubao-seedance-2-5-260628" ? 30 : 15;
  if (!Number.isInteger(duration) || (duration !== -1 && (duration < minDuration || duration > maxDuration))) {
    throw new Error(`该视频模型的视频时长必须为自动，或 ${minDuration} 至 ${maxDuration} 秒的整数`);
  }

  const assetIds = Array.isArray(draft.assetIds)
    ? draft.assetIds.map(stringValue).filter(Boolean)
    : [];
  if (draft.method !== "text" && assetIds.length === 0) {
    throw new Error("图生视频或参考生成需要先添加素材");
  }

  return {
    prompt,
    modelId,
    method: draft.method,
    ratio: draft.ratio,
    durationSeconds: duration,
    generateAudio: normalizeAudio(draft.generateAudio ?? draft.audio),
    resolution: draft.resolution,
    assetIds,
  };
}

export function buildVideoCreateRequest(value: unknown, requestId: string): VideoCreateRequest {
  const normalizedRequestId = stringValue(requestId);
  if (!normalizedRequestId) throw new Error("视频请求缺少幂等标识");
  return { requestId: normalizedRequestId, ...normalizeVideoDraft(value) };
}

type Listener = (snapshot: VideoControlSnapshot) => void;

export class VideoControlStore {
  private configured = false;
  private currentState: VideoControlState = "unconfigured";
  private taskId: string | null = null;
  private requestId: string | null = null;
  private modelId: VideoModelId | null = null;
  private reason = "视频服务尚未配置";
  private readonly listeners = new Set<Listener>();

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  setCapability(available: boolean, reason = ""): void {
    this.configured = available;
    this.reason = reason;
    if (!available && !["queued", "running", "submitting", "recovering", "cancelling", "archiving"].includes(this.currentState)) {
      this.currentState = "unconfigured";
    } else if (available && this.currentState === "unconfigured") {
      this.currentState = "ready";
    }
    this.emit();
  }

  beginCreate(request: VideoCreateRequest): boolean {
    if (!this.configured || !this.snapshot().canCreate) return false;
    this.currentState = "submitting";
    this.requestId = request.requestId;
    this.modelId = request.modelId;
    this.taskId = null;
    this.reason = "正在等待服务器确认视频任务";
    this.emit();
    return true;
  }

  createAccepted(taskId: string, status: VideoTaskStatus): void {
    if (!oneOf(status, VIDEO_TASK_STATUSES)) throw new Error("服务器返回了未知视频任务状态");
    this.taskId = stringValue(taskId) || null;
    this.currentState = status;
    this.reason = "";
    this.emit();
  }

  createFailed(message: string): void {
    this.currentState = this.configured ? "ready" : "unconfigured";
    this.reason = stringValue(message) || "视频任务未能提交";
    this.taskId = null;
    this.emit();
  }

  restorePendingCreate(request: VideoCreateRequest, message = "正在确认上一次视频请求的真实结果"): void {
    this.currentState = "recovering";
    this.requestId = request.requestId;
    this.modelId = request.modelId;
    this.taskId = null;
    this.reason = message;
    this.emit();
  }

  restoreTask(task: RestoredVideoTask): boolean {
    const taskId = stringValue(task.taskId);
    const states = ["queued", "running", "recovering", "archiving", "cancelled", "succeeded", "failed", "expired"] as const;
    if (!taskId || !oneOf(task.state, states)) return false;
    if (task.modelId != null && !oneOf(task.modelId, VIDEO_MODEL_IDS)) return false;
    this.taskId = taskId;
    this.currentState = task.state;
    this.requestId = stringValue(task.requestId) || null;
    this.modelId = task.modelId || null;
    this.reason = stringValue(task.reason);
    if (task.state === "archiving" && !this.reason) {
      this.reason = "服务商已完成生成，服务器正在留存并校验原片";
    }
    this.emit();
    return true;
  }

  markTaskRecovering(taskId: string, message: string): boolean {
    const normalizedTaskId = stringValue(taskId);
    if (!normalizedTaskId || (this.taskId && normalizedTaskId !== this.taskId)) return false;
    this.taskId = normalizedTaskId;
    this.currentState = "recovering";
    this.reason = stringValue(message) || "服务器正在恢复任务状态；不会重复创建新任务";
    this.emit();
    return true;
  }

  resetForPrincipalChange(message = "登录账户已切换，旧账户的视频任务已从当前界面移除"): void {
    this.currentState = this.configured ? "ready" : "unconfigured";
    this.taskId = null;
    this.requestId = null;
    this.modelId = null;
    this.reason = message;
    this.emit();
  }

  createOutcomeUnknown(message: string): void {
    this.currentState = "recovering";
    this.reason = stringValue(message) || "视频创建结果尚未确认，正在使用同一幂等标识恢复";
    this.emit();
  }

  beginCancelQueued(): boolean {
    if (this.currentState !== "queued" || !this.taskId) return false;
    this.currentState = "cancelling";
    this.reason = "正在等待服务器确认取消排队";
    this.emit();
    return true;
  }

  cancelFailed(message: string): void {
    if (!this.taskId) return;
    this.currentState = "queued";
    this.reason = stringValue(message) || "取消排队失败";
    this.emit();
  }

  markArchiving(taskId: string): boolean {
    const normalizedTaskId = stringValue(taskId);
    if (!normalizedTaskId || (this.taskId && normalizedTaskId !== this.taskId)) return false;
    this.taskId = normalizedTaskId;
    this.currentState = "archiving";
    this.reason = "服务商已完成生成，服务器正在留存并校验原片";
    this.emit();
    return true;
  }

  applyTaskEvent(data: Record<string, unknown>): boolean {
    const eventTaskId = stringValue(data.taskId);
    if (!eventTaskId || (this.taskId && eventTaskId !== this.taskId)) return false;
    if (!oneOf(data.status, VIDEO_TASK_STATUSES)) return false;
    this.taskId = eventTaskId;
    this.currentState = data.status;
    this.reason = data.error && typeof data.error === "object"
      ? stringValue((data.error as Record<string, unknown>).message)
      : stringValue(data.label);
    this.emit();
    return true;
  }

  snapshot(): VideoControlSnapshot {
    const terminal = ["succeeded", "failed", "expired", "cancelled"];
    return {
      state: this.currentState,
      taskId: this.taskId,
      requestId: this.requestId,
      modelId: this.modelId,
      reason: this.reason,
      canCreate: this.configured && (this.currentState === "ready" || terminal.includes(this.currentState)),
      canCancelQueued: this.currentState === "queued" && Boolean(this.taskId),
      canStopRunning: false,
    };
  }

  private emit(): void {
    const snapshot = this.snapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}
